import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadApprovalAPI } from '../api';
import { toast } from 'sonner';
import { ClipboardCheck, Check, X, Edit3, Phone } from 'lucide-react';
import { format } from 'date-fns';

export const LeadApprovalsPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await leadApprovalAPI.pending();
      setLeads(data.leads || []);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, id, label) => {
    try { await fn(); toast.success(label); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };
  const approve = (id) => act(() => leadApprovalAPI.approve(id), id, 'Lead approved');
  const reject = (id) => {
    const r = window.prompt('Reason for rejection?', 'Not a valid lead');
    if (r === null) return;
    act(() => leadApprovalAPI.reject(id, r), id, 'Lead rejected');
  };
  const requestChanges = (id) => {
    const r = window.prompt('What changes are required?', '');
    if (r === null) return;
    act(() => leadApprovalAPI.requestChanges(id, r), id, 'Changes requested');
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6" data-testid="lead-approvals-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/10"><ClipboardCheck className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="text-3xl font-outfit font-semibold">Self-Created Leads — Pending Approval</h1>
          <p className="text-muted-foreground">Review, approve or reject leads created by your team.</p>
        </div>
      </div>

      <div className="clay-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="approvals-table">
            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
              <tr>
                {['Lead Name', 'Mobile', 'Created By', 'Assigned To', 'Source', 'City', 'Created', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {leads.map(l => (
                <tr key={l.id} data-testid={`approval-row-${l.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/leads/${l.id}`)} className="text-primary hover:underline font-medium">{l.customer_name}</button>
                  </td>
                  <td className="px-4 py-3 text-sm flex items-center gap-1"><Phone className="h-3 w-3 text-muted-foreground" />{l.primary_mobile}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{l.created_by_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{l.assigned_to_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{l.lead_source || '—'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{l.city || '—'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{l.created_at ? format(new Date(l.created_at), 'MMM d, h:mm a') : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => approve(l.id)} data-testid={`approve-${l.id}`} title="Approve"
                        className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"><Check className="h-4 w-4" /></button>
                      <button onClick={() => requestChanges(l.id)} data-testid={`request-changes-${l.id}`} title="Request Changes"
                        className="p-1.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => reject(l.id)} data-testid={`reject-${l.id}`} title="Reject"
                        className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200"><X className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">
                  <ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No leads pending approval</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
export default LeadApprovalsPage;
